import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PagedDto } from '../../../models/paged.dto';
import {
  DynamicWorkflowDefinition,
  DynamicFieldValue,
  DynamicRow,
  DynamicWorkflowInstance,
  DynamicWorkflowInstanceListItem,
  DynamicWorkflowSummary,
  DynamicWorkflowValidation,
  DynamicWorkflowWrite,
} from './dynamic-framework.models';

export function dynamicApiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof HttpErrorResponse)) return fallback;
  if (typeof error.error === 'string') return error.error || fallback;
  if (typeof error.error !== 'object') return fallback;
  const body = error.error as {
    detail?: unknown;
    error?: unknown;
    errors?: Record<string, unknown>;
  } | null;
  const validationMessage = Object.values(body?.errors ?? {})
    .flatMap(value => Array.isArray(value) ? value : [value])
    .find((value): value is string => typeof value === 'string' && !!value.trim());
  if (validationMessage) return validationMessage;
  if (typeof body?.detail === 'string') return body.detail;
  if (typeof body?.error === 'string') return body.error;
  return fallback;
}

@Injectable({ providedIn: 'root' })
export class DynamicFrameworkApiService {
  constructor(private readonly http: HttpClient) {}

  listWorkflows(query: {
    search?: string;
    status?: 'draft' | 'published';
    sort?: string;
    desc?: boolean;
    page?: number;
    pageSize?: number;
  } = {}): Observable<PagedDto<DynamicWorkflowSummary>> {
    let params = new HttpParams();
    if (query.search) params = params.set('search', query.search);
    if (query.status) params = params.set('status', query.status);
    if (query.sort) params = params.set('sort', query.sort);
    if (query.desc != null) params = params.set('desc', query.desc);
    if (query.page != null) params = params.set('page', query.page);
    if (query.pageSize != null) params = params.set('pageSize', query.pageSize);
    return this.http.get<PagedDto<DynamicWorkflowSummary>>(
      '/api/dynamic-workflows',
      { params },
    );
  }

  getWorkflow(workflowKey: string): Observable<DynamicWorkflowDefinition> {
    return this.http.get<DynamicWorkflowDefinition>(`/api/dynamic-workflows/${workflowKey}`);
  }

  createWorkflow(write: DynamicWorkflowWrite): Observable<DynamicWorkflowDefinition> {
    return this.http.post<DynamicWorkflowDefinition>('/api/dynamic-workflows', write);
  }

  updateWorkflow(
    workflowKey: string,
    write: DynamicWorkflowWrite,
  ): Observable<DynamicWorkflowDefinition> {
    return this.http.put<DynamicWorkflowDefinition>(`/api/dynamic-workflows/${workflowKey}`, write);
  }

  validateWorkflow(write: DynamicWorkflowWrite): Observable<DynamicWorkflowValidation> {
    return this.http.post<DynamicWorkflowValidation>('/api/dynamic-workflows/validate', write);
  }

  publishWorkflow(
    workflowKey: string,
    expectedUpdatedAt: string,
    startNodeKey: string,
  ): Observable<DynamicWorkflowDefinition> {
    return this.http.post<DynamicWorkflowDefinition>(
      `/api/dynamic-workflows/${workflowKey}/publish`,
      { expectedUpdatedAt, startNodeKey },
    );
  }

  unpublishWorkflow(
    workflowKey: string,
    expectedUpdatedAt: string,
  ): Observable<DynamicWorkflowDefinition> {
    return this.http.post<DynamicWorkflowDefinition>(
      `/api/dynamic-workflows/${workflowKey}/unpublish`,
      { expectedUpdatedAt },
    );
  }

  deleteWorkflow(workflowKey: string, expectedUpdatedAt: string): Observable<void> {
    const params = new HttpParams().set('expectedUpdatedAt', expectedUpdatedAt);
    return this.http.delete<void>(`/api/dynamic-workflows/${workflowKey}`, { params });
  }

  listWorkflowInstances(
    workflowKey: string,
    query: {
      nodeKey?: string;
      sort?: string;
      desc?: boolean;
      page?: number;
      pageSize?: number;
      /** Filter-bar rules, each already spelled `field:op:value`. */
      rules?: readonly string[];
    } = {},
  ): Observable<PagedDto<DynamicWorkflowInstanceListItem>> {
    let params = new HttpParams();
    if (query.nodeKey) params = params.set('nodeKey', query.nodeKey);
    if (query.sort) params = params.set('sort', query.sort);
    if (query.desc != null) params = params.set('desc', query.desc);
    if (query.page != null) params = params.set('page', query.page);
    if (query.pageSize != null) params = params.set('pageSize', query.pageSize);
    // Appended, not set: `rule` repeats, one parameter per rule, and the
    // server ANDs them. Setting would leave only the last one filtering.
    for (const rule of query.rules ?? []) params = params.append('rule', rule);
    return this.http.get<PagedDto<DynamicWorkflowInstanceListItem>>(
      `/api/dynamic-workflows/${workflowKey}/instances`,
      { params },
    );
  }

  exportWorkflowRecords(workflowKey: string): Observable<Blob> {
    return this.http.get(
      `/api/dynamic-workflows/${workflowKey}/export`,
      { responseType: 'blob' },
    );
  }

  createWorkflowRecord(
    workflowKey: string,
    values: DynamicFieldValue[],
  ): Observable<DynamicWorkflowInstance> {
    return this.http.post<DynamicWorkflowInstance>(
      `/api/dynamic-workflows/${workflowKey}/records`,
      { values },
    );
  }

  updateWorkflowCurrentRow(
    workflowKey: string,
    instanceId: number,
    expectedVersion: number,
    values: DynamicFieldValue[],
  ): Observable<DynamicRow> {
    return this.http.put<DynamicRow>(
      `/api/dynamic-workflows/${workflowKey}/instances/${instanceId}/current-row`,
      { expectedVersion, values },
    );
  }

  deleteWorkflowRecord(
    workflowKey: string,
    instanceId: number,
    expectedVersion: number,
    expectedRowVersion: number,
  ): Observable<void> {
    const params = new HttpParams()
      .set('expectedVersion', expectedVersion)
      .set('expectedRowVersion', expectedRowVersion);
    return this.http.delete<void>(
      `/api/dynamic-workflows/${workflowKey}/instances/${instanceId}`,
      { params },
    );
  }
}
